import { useState, useEffect, useRef, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Circle, Cloud, Download, Hexagon, Highlighter, Layers, MessageSquare, Minus, MoveHorizontal, Pencil, Ruler, Square, Type, Waypoints } from '@/components/ui/icons';
import { openLocalPdf } from '@/features/local/openLocalPdf';
import { UnitSystem } from '@/types';
import { browserDefaultUnitSystem } from '@/core/units';
import { createLogger } from '@/utils/logger';
import { Button, Input, Select } from '@/components/ui/primitives';
import { classifyError } from '@/analytics';
import { useFeedback } from '@/context/FeedbackContext';
import { Dateline, FeatureGrid, MarketingPage, type SiteLocale } from '@/components/marketing';
import { pageForRoute, type PageRoute } from '@/site/pageRegistry';
import { type LandingPageContent, LANDING_HREFLANG_ALTERNATES } from '@/pages/landingContent';

import '@/styles/pages/LandingPage.css';
import '@/styles/pages/ViewerPage.css';

const log = createLogger('LocalizedLandingPage');

const PDF_SIGNATURE_BYTES = [0x25, 0x50, 0x44, 0x46, 0x2d] as const;

async function readBlobBytes(blob: Blob): Promise<Uint8Array> {
    if (typeof blob.arrayBuffer === 'function') {
        return new Uint8Array(await blob.arrayBuffer());
    }
    const result = await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
        reader.readAsArrayBuffer(blob);
    });
    return new Uint8Array(result);
}

async function hasPdfSignature(file: File): Promise<boolean> {
    const header = await readBlobBytes(file.slice(0, PDF_SIGNATURE_BYTES.length));
    return PDF_SIGNATURE_BYTES.every((byte, index) => header[index] === byte);
}

// Tool icons and images are locale-independent; only name/desc come from content.
const TOOL_ICONS: ReadonlyArray<{ icon: typeof Minus; img?: string }> = [
    { icon: MoveHorizontal, img: '/tool-shots/dimension.jpg' },
    { icon: Minus, img: '/tool-shots/line.jpg' },
    { icon: Waypoints, img: '/tool-shots/polyline.jpg' },
    { icon: Hexagon, img: '/tool-shots/polygon.jpg' },
    { icon: Square, img: '/tool-shots/rectangle.jpg' },
    { icon: Circle, img: '/tool-shots/ellipse.jpg' },
    { icon: Cloud, img: '/tool-shots/cloud.jpg' },
    { icon: ArrowUpRight, img: '/tool-shots/arrow.jpg' },
    { icon: Pencil, img: '/tool-shots/freehand.jpg' },
    { icon: Highlighter, img: '/tool-shots/highlight.jpg' },
    { icon: MessageSquare, img: '/tool-shots/callout.jpg' },
    { icon: Type, img: '/tool-shots/text.jpg' },
    { icon: Ruler },
    { icon: Layers },
    { icon: Download },
];

const LOCALE_FOR_ROUTE: Partial<Record<PageRoute, SiteLocale>> = {
    '/': 'en',
    '/de': 'de',
    '/fr': 'fr',
};

interface LocalizedLandingPageProps {
    route: PageRoute;
    content: LandingPageContent;
}

export function LocalizedLandingPage({ route, content }: LocalizedLandingPageProps) {
    const navigate = useNavigate();
    const { show } = useFeedback();
    const PAGE = pageForRoute(route);
    // The top bar's Open a PDF — free starts the hero's file picker (the existing open-file flow). While a file is
    // already picked the input is gone and the bar's link just scrolls to the drop zone (#open).
    const fileInputRef = useRef<HTMLInputElement>(null);
    const openPicker = () => fileInputRef.current?.click();

    const handleUpload = async (file: File, unitSystem: UnitSystem) => {
        try {
            navigate(await openLocalPdf(file, unitSystem));
        } catch (err) {
            log.error('Error uploading file:', err);
            show(content.hero.errors[classifyError(err)], 'error');
            throw err;
        }
    };

    return (
        <MarketingPage head={{
            ...PAGE.head,
            canonical: PAGE.canonical,
            lang: LOCALE_FOR_ROUTE[route],
            alternates: LANDING_HREFLANG_ALTERNATES,
        }} siteHeader={{ content: content.header, landing: { locale: LOCALE_FOR_ROUTE[route] ?? 'en', onOpenPdf: openPicker } }}>
            <main>
                <HeroSection content={content} onUpload={handleUpload} fileInputRef={fileInputRef} />
                <ToolCarousel content={content} />
                <FeatureGrid
                    id="how-it-works"
                    sectionTitle={content.featureGrid.sectionTitle}
                    sectionLead={content.featureGrid.sectionLead}
                    cards={content.featureGrid.cards.map((c) => ({ ...c }))}
                />
                <BimDataSection content={content} />
                <Dateline reviewed={PAGE.reviewed} locale={content.locale} />
            </main>
            <Footer content={content} />
        </MarketingPage>
    );
}

/* ===================================
   HERO SECTION
   =================================== */

interface HeroSectionProps {
    content: LandingPageContent;
    onUpload: (file: File, unitSystem: UnitSystem) => Promise<void>;
    /** The file input, for the top bar's Open a PDF — free. */
    fileInputRef: RefObject<HTMLInputElement>;
}

function HeroSection({ content, onUpload, fileInputRef }: HeroSectionProps) {
    const { show } = useFeedback();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [unitSystem, setUnitSystem] = useState<UnitSystem>(browserDefaultUnitSystem);
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleCandidateFile = async (file: File | null | undefined) => {
        if (!file) return;
        try {
            if (await hasPdfSignature(file)) {
                setSelectedFile(file);
                return;
            }
            setSelectedFile(null);
            show(content.hero.invalidPdfMessage, 'error');
        } catch {
            setSelectedFile(null);
            show(content.hero.unreadablePdfMessage, 'error');
        }
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        await handleCandidateFile(e.dataTransfer.files?.[0]);
    };

    const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
        await handleCandidateFile(e.target.files?.[0]);
    };

    const handleUploadClick = async () => {
        if (!selectedFile) return;
        setUploading(true);
        try {
            await onUpload(selectedFile, unitSystem);
        } catch {
            setUploading(false);
        }
    };

    const hero = content.hero;

    return (
        <section className="hero-section" id="open">
            <div className="landing-container">
                <h1 className="hero-title">
                    {hero.titleLine1}<br />{hero.titleLine2}
                </h1>
                <p className="hero-subtitle">{hero.subtitle}</p>
                <div
                    className={`hero-upload ${dragActive ? 'hero-upload--active' : ''}`}
                    data-testid="drop-zone"
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    {selectedFile ? (
                        <div className="hero-upload__selected">
                            <svg className="hero-upload__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="hero-upload__filename">{selectedFile.name}</p>
                            <p className="hero-upload__filesize">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>

                            <div className="hero-upload__options">
                                <Select value={unitSystem} onChange={(e) => setUnitSystem(e.target.value as UnitSystem)} className="hero-upload__select" aria-label={hero.unitLabel}>
                                    <option value={UnitSystem.IMPERIAL}>{hero.unitImperial}</option>
                                    <option value={UnitSystem.METRIC}>{hero.unitMetric}</option>
                                </Select>
                            </div>

                            <div className="hero-upload__actions">
                                <Button onClick={handleUploadClick} loading={uploading} variant="primary" size="lg" data-testid="open-pdf-btn">
                                    {uploading ? hero.loading : hero.startMarkup}
                                </Button>
                                <Button onClick={() => setSelectedFile(null)} disabled={uploading} variant="ghost">
                                    {hero.differentFile}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="hero-upload__prompt">
                            <svg className="hero-upload__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <p className="hero-upload__text">{hero.dropPrompt}</p>
                            <p className="hero-upload__or">{hero.or}</p>
                            <label className="hero-upload__label">
                                <Input ref={fileInputRef} type="file" accept="application/pdf" onChange={handleFileInput} className="hero-upload__input" aria-label={hero.chooseFile} data-testid="file-input" />
                                <span className="bk-button bk-button--primary bk-button--lg">{hero.chooseFile}</span>
                            </label>
                        </div>
                    )}
                </div>

                <ul className="hero-chips">
                    <li className="hero-chip">{hero.chipNothingUploaded}</li>
                    <li className="hero-chip">{hero.chipFree}</li>
                    <li className="hero-chip" data-testid="hero-chip-first-visit">{hero.chipOffline}</li>
                    <li className="hero-chip">{hero.chipNoAccount}</li>
                </ul>
            </div>
        </section>
    );
}

/* ===================================
   TOOL CAROUSEL
   =================================== */

const CAROUSEL_INTERVAL_MS = 2600;

function ToolCarousel({ content }: { content: LandingPageContent }) {
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);
    const tools = content.toolShowcase.tools;

    useEffect(() => {
        if (paused) return;
        const id = setInterval(
            () => setActive((a) => (a + 1) % tools.length),
            CAROUSEL_INTERVAL_MS
        );
        return () => clearInterval(id);
    }, [paused, tools.length]);

    const currentContent = tools[active];
    const currentIcons = TOOL_ICONS[active];
    const CurrentIcon = currentIcons.icon;

    return (
        <section
            className="carousel-section"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="landing-container">
                <h2 className="section-title">{content.toolShowcase.sectionTitle}</h2>
                <p className="section-lead">{content.toolShowcase.sectionLead}</p>

                <div className="carousel">
                    <div className="carousel__stage" aria-live="polite">
                        {currentIcons.img ? (
                            <div className="carousel__shot" key={active}>
                                <img src={currentIcons.img} alt={content.toolShowcase.shotAlt.replace('{tool}', currentContent.name)} loading="lazy" />
                            </div>
                        ) : (
                            <div className="carousel__icon" key={active}>
                                <CurrentIcon strokeWidth={1.5} />
                            </div>
                        )}
                        <h3 className="carousel__name">{currentContent.name}</h3>
                        <p className="carousel__desc">{currentContent.desc}</p>
                    </div>

                    <div className="carousel__rail" role="tablist" aria-label={content.toolShowcase.carouselAriaLabel}>
                        {tools.map((tool, i) => {
                            const Icon = TOOL_ICONS[i].icon;
                            return (
                                <Button
                                    key={tool.name}
                                    icon={Icon}
                                    variant="ghost"
                                    role="tab"
                                    aria-selected={i === active}
                                    aria-label={tool.name}
                                    tooltip={tool.name}
                                    className={`carousel__tool${i === active ? ' is-on' : ''}`}
                                    onClick={() => setActive(i)}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ===================================
   BIM DATA SECTION
   =================================== */

function BimDataSection({ content }: { content: LandingPageContent }) {
    const bim = content.bimData;
    return (
        <section className="bim-data-section">
            <div className="landing-container bim-data-split">
                <div className="bim-data-visual" aria-hidden="true">
                    <div className="bim-data-visual__sheet">
                        <div className="bim-data-visual__room bim-data-visual__room--a" />
                        <div className="bim-data-visual__room bim-data-visual__room--b" />
                        <div className="bim-data-visual__wall" />
                    </div>
                    <div className="bim-data-visual__popup">
                        <div className="bim-data-visual__popup-title">Wall &mdash; Exterior CMU</div>
                        <dl className="bim-data-visual__popup-list">
                            <div><dt>Level</dt><dd>02 &mdash; Second Floor</dd></div>
                            <div><dt>Type</dt><dd>CMU-8&quot; Grout Fill</dd></div>
                            <div><dt>Fire Rating</dt><dd>2 HR</dd></div>
                            <div><dt>Element ID</dt><dd>483920</dd></div>
                        </dl>
                    </div>
                </div>
                <div className="bim-data-copy">
                    <h2 className="section-title bim-data-copy__title">{bim.sectionTitle}</h2>
                    <p className="section-lead bim-data-copy__lead">{bim.sectionLead}</p>
                    <ul className="bim-data-list">
                        {bim.items.map((it) => (
                            <li key={it.title} className="bim-data-list__item">
                                <span className="bim-data-list__title">{it.title}</span>
                                <span className="bim-data-list__body">{it.body}</span>
                            </li>
                        ))}
                    </ul>
                    <a className="bim-data-section__link" href="https://bimpdf.com" target="_blank" rel="noreferrer">
                        {bim.learnMore}
                    </a>
                </div>
            </div>
        </section>
    );
}

/* ===================================
   FOOTER
   =================================== */

import { FOOTER_NAV_LINKS } from '@/site/crawler';

function Footer({ content }: { content: LandingPageContent }) {
    const footer = content.footer;
    return (
        <footer className="footer">
            <div className="landing-container">
                <p className="footer-text">
                    PDF Markup Tool -- &copy; 2026 Small Tiger Group
                    {' · '}
                    <span title={`commit ${__BUILD_COMMIT__} · built ${__BUILD_DATE__}`}>
                        v{__APP_VERSION__}
                    </span>
                </p>
                <p className="footer-text" style={{marginTop: 'var(--space-2)', fontSize: '0.85em'}}>
                    {FOOTER_NAV_LINKS.map(({ route, href, label }, i) => (
                        <span key={route}>
                            {i > 0 && ' · '}
                            <a href={href} style={{color: 'inherit'}}>{footer.navLabels[route] ?? label}</a>
                        </span>
                    ))}
                </p>
                <p className="footer-text" style={{marginTop: 'var(--space-2)', fontSize: '0.85em'}}>
                    {footer.relatedTools}{' '}
                    <a href="https://pdfdifftool.com" style={{color: 'inherit'}}>{footer.comparePdfs}</a>
                    {' · '}
                    <a href="https://pdfflattener.com" style={{color: 'inherit'}}>{footer.flattenPdf}</a>
                    {' · '}
                    <a href="https://pdffillandsign.com" style={{color: 'inherit'}}>{footer.fillAndSign}</a>
                </p>
            </div>
        </footer>
    );
}
